<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import integrationAPI from 'dashboard/api/integrations';
import {
  eventDisplayName as displayNameForEvent,
  eventInventoryLabel as inventoryLabelForEvent,
  eventLineItemCount,
  eventMetadataSummary as metadataSummaryForEvent,
  eventProductImageAlt as productImageAltForEvent,
  eventProductImageUrl,
  eventProductUrl,
  eventValueLabel,
} from 'dashboard/components-next/Contacts/helpers/klaviyoEventMetadata';
import CrmEventCreateDialog from 'dashboard/components-next/Contacts/CrmEvents/CrmEventCreateDialog.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

/**
 * @typedef {Object} CrmEventContact
 * @property {number|string} id
 * @property {string|null} name
 * @property {string|null} email
 * @property {string|null} phone_number
 */

/**
 * @typedef {Object} CrmFeedEvent
 * @property {number|string} id
 * @property {string|null} event_name
 * @property {string|null} metric_name
 * @property {string|null} sync_status
 * @property {string} occurred_at
 * @property {string|number|null} value
 * @property {Record<string, unknown>} metadata
 * @property {CrmEventContact|null} contact
 */

const DEFAULT_PER_PAGE = 25;
const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const EVENT_GROUPS = {
  TODAY: 'today',
  LAST_WEEK: 'last_week',
  LAST_MONTH: 'last_month',
  OLDER: 'older',
};

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const isLoading = ref(false);
const errorMessage = ref('');
/** @type {import('vue').Ref<CrmFeedEvent[]>} */
const events = ref([]);
const meta = ref({
  current_page: 1,
  per_page: DEFAULT_PER_PAGE,
  count: 0,
  has_more: false,
});
/** @type {import('vue').Ref<Set<string>>} */
const failedProductImageEventIds = ref(new Set());
const createDialogRef = ref(null);
const helpDialogRef = ref(null);

const currentPage = computed(() => Number(meta.value.current_page || 1));
const hasPreviousPage = computed(() => currentPage.value > 1);
const hasNextPage = computed(() => Boolean(meta.value.has_more));
const pageLabel = computed(() =>
  t('CRM_EVENTS_FEED.PAGE', { page: currentPage.value })
);
const countLabel = computed(() =>
  t('CRM_EVENTS_FEED.COUNT', { count: meta.value.count || 0 })
);
const paginationLabel = computed(
  () => `${pageLabel.value} / ${countLabel.value}`
);
const syncStatusLabels = computed(() => ({
  pending: t('CRM_EVENTS_FEED.SYNC_STATUS.PENDING'),
  sent: t('CRM_EVENTS_FEED.SYNC_STATUS.SENT'),
  failed: t('CRM_EVENTS_FEED.SYNC_STATUS.FAILED'),
  ignored: t('CRM_EVENTS_FEED.SYNC_STATUS.IGNORED'),
  received: t('CRM_EVENTS_FEED.SYNC_STATUS.RECEIVED'),
}));
const helpSections = computed(() => [
  {
    key: 'sources',
    title: t('CRM_EVENTS_FEED.HELP.SECTIONS.SOURCES.TITLE'),
    bullets: [
      t('CRM_EVENTS_FEED.HELP.SECTIONS.SOURCES.BULLET_1'),
      t('CRM_EVENTS_FEED.HELP.SECTIONS.SOURCES.BULLET_2'),
      t('CRM_EVENTS_FEED.HELP.SECTIONS.SOURCES.BULLET_3'),
    ],
  },
  {
    key: 'statuses',
    title: t('CRM_EVENTS_FEED.HELP.SECTIONS.STATUSES.TITLE'),
    bullets: [
      t('CRM_EVENTS_FEED.HELP.SECTIONS.STATUSES.BULLET_1'),
      t('CRM_EVENTS_FEED.HELP.SECTIONS.STATUSES.BULLET_2'),
      t('CRM_EVENTS_FEED.HELP.SECTIONS.STATUSES.BULLET_3'),
      t('CRM_EVENTS_FEED.HELP.SECTIONS.STATUSES.BULLET_4'),
      t('CRM_EVENTS_FEED.HELP.SECTIONS.STATUSES.BULLET_5'),
    ],
  },
  {
    key: 'manual_events',
    title: t('CRM_EVENTS_FEED.HELP.SECTIONS.MANUAL_EVENTS.TITLE'),
    bullets: [
      t('CRM_EVENTS_FEED.HELP.SECTIONS.MANUAL_EVENTS.BULLET_1'),
      t('CRM_EVENTS_FEED.HELP.SECTIONS.MANUAL_EVENTS.BULLET_2'),
      t('CRM_EVENTS_FEED.HELP.SECTIONS.MANUAL_EVENTS.BULLET_3'),
    ],
  },
  {
    key: 'admins',
    title: t('CRM_EVENTS_FEED.HELP.SECTIONS.ADMINS.TITLE'),
    bullets: [
      t('CRM_EVENTS_FEED.HELP.SECTIONS.ADMINS.BULLET_1'),
      t('CRM_EVENTS_FEED.HELP.SECTIONS.ADMINS.BULLET_2'),
      t('CRM_EVENTS_FEED.HELP.SECTIONS.ADMINS.BULLET_3'),
    ],
  },
]);

/**
 * Converts route query input into a positive integer.
 *
 * @param {unknown} value Route query value.
 * @param {number} fallback Fallback value.
 * @returns {number}
 */
function positiveInteger(value, fallback) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

/**
 * Returns the local start-of-day for bucket comparisons.
 *
 * @param {Date} date Date to normalize.
 * @returns {Date}
 */
function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Returns the recent activity bucket for an event timestamp.
 *
 * @param {CrmFeedEvent} event CRM event.
 * @returns {string}
 */
function eventGroupKey(event) {
  const occurredAt = new Date(event.occurred_at);
  if (Number.isNaN(occurredAt.getTime())) return EVENT_GROUPS.OLDER;

  const today = startOfLocalDay(new Date());
  const eventDay = startOfLocalDay(occurredAt);
  const dayDifference = Math.floor((today - eventDay) / DAY_IN_MILLISECONDS);

  if (dayDifference <= 0) return EVENT_GROUPS.TODAY;
  if (dayDifference <= 7) return EVENT_GROUPS.LAST_WEEK;
  if (dayDifference <= 30) return EVENT_GROUPS.LAST_MONTH;
  return EVENT_GROUPS.OLDER;
}

/**
 * Maps an activity bucket to a translated label.
 *
 * @param {string} key Activity bucket key.
 * @returns {string}
 */
function eventGroupLabel(key) {
  if (key === EVENT_GROUPS.TODAY) return t('CRM_EVENTS_FEED.GROUPS.TODAY');
  if (key === EVENT_GROUPS.LAST_WEEK) {
    return t('CRM_EVENTS_FEED.GROUPS.LAST_WEEK');
  }
  if (key === EVENT_GROUPS.LAST_MONTH) {
    return t('CRM_EVENTS_FEED.GROUPS.LAST_MONTH');
  }
  return t('CRM_EVENTS_FEED.GROUPS.OLDER');
}

const groupedEvents = computed(() => {
  const groups = [];

  events.value.forEach(event => {
    const key = eventGroupKey(event);
    let group = groups.find(item => item.key === key);

    if (!group) {
      group = {
        key,
        label: eventGroupLabel(key),
        events: [],
      };
      groups.push(group);
    }

    group.events.push(event);
  });

  return groups;
});

/**
 * Reads the current route query into API parameters.
 *
 * @returns {Record<string, string|number>}
 */
function requestParams() {
  return {
    page: positiveInteger(route.query.page, 1),
    per_page: DEFAULT_PER_PAGE,
  };
}

/**
 * Loads one CRM events page using route query filters.
 *
 * @returns {Promise<void>}
 */
async function loadEvents() {
  isLoading.value = true;
  errorMessage.value = '';
  failedProductImageEventIds.value = new Set();

  try {
    const response = await integrationAPI.getCrmEvents(requestParams());
    events.value = response.data.items || [];
    meta.value = {
      current_page: response.data.meta?.current_page || 1,
      per_page: response.data.meta?.per_page || DEFAULT_PER_PAGE,
      count: response.data.meta?.count || 0,
      has_more: response.data.meta?.has_more || false,
    };
  } catch {
    errorMessage.value = t('CRM_EVENTS_FEED.ERROR');
  } finally {
    isLoading.value = false;
  }
}

/**
 * Builds a route query for feed navigation.
 *
 * @param {Object} overrides Query overrides.
 * @returns {Record<string, string|number>}
 */
function nextQuery(overrides = {}) {
  return {
    page: positiveInteger(route.query.page, 1),
    ...overrides,
  };
}

/**
 * Navigates to a feed page.
 *
 * @param {number} page Target page.
 * @returns {void}
 */
function goToPage(page) {
  router.replace({ query: nextQuery({ page }) });
}

/**
 * Returns a stable contact URL for an event contact.
 *
 * @param {CrmFeedEvent} event CRM event.
 * @returns {string}
 */
function contactUrl(event) {
  return `/app/accounts/${route.params.accountId}/contacts/${event.contact?.id}`;
}

/**
 * Formats a CRM event timestamp for compact feed display.
 *
 * @param {string} value ISO timestamp.
 * @returns {string}
 */
function formatOccurredAt(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

/**
 * Returns the primary display label for an event.
 *
 * @param {CrmFeedEvent} event CRM event.
 * @returns {string}
 */
const eventDisplayName = event =>
  displayNameForEvent(event, t('CRM_EVENTS_FEED.EVENT'));

/**
 * Builds a compact metadata summary for the feed row.
 *
 * @param {CrmFeedEvent} event CRM event.
 * @returns {string}
 */
function eventMetadataSummary(event) {
  const lineItemCount = eventLineItemCount(event);
  return metadataSummaryForEvent(event, {
    itemCountLabel: lineItemCount
      ? t('CONTACT_PANEL.CRM.ITEM_COUNT', { count: lineItemCount })
      : null,
    inventoryLabel: inventoryLabelForEvent(event, {
      availableLabel: ({ count }) =>
        t('CONTACT_PANEL.CRM.INVENTORY_AVAILABLE', { count }),
      availableAtLocationLabel: ({ count, location }) =>
        t('CONTACT_PANEL.CRM.INVENTORY_AVAILABLE_AT_LOCATION', {
          count,
          location,
        }),
      availableAtLocationsLabel: ({ count, locationCount }) =>
        t('CONTACT_PANEL.CRM.INVENTORY_AVAILABLE_AT_LOCATIONS', {
          count,
          locationCount,
        }),
    }),
  });
}

/**
 * Returns a stable key for product image error tracking.
 *
 * @param {CrmFeedEvent} event CRM event.
 * @returns {string}
 */
const imageFailureKey = event => String(event.id || event.occurred_at);

/**
 * Checks whether the product image failed during this render session.
 *
 * @param {CrmFeedEvent} event CRM event.
 * @returns {boolean}
 */
const hasProductImageFailed = event =>
  failedProductImageEventIds.value.has(imageFailureKey(event));

/**
 * Opens the manual CRM event creation dialog.
 *
 * @returns {void}
 */
function openCreateDialog() {
  createDialogRef.value?.open();
}

/**
 * Opens the CRM event behavior reference dialog.
 *
 * @returns {void}
 */
function openHelpDialog() {
  helpDialogRef.value?.open();
}

/**
 * Closes the CRM event behavior reference dialog.
 *
 * @returns {void}
 */
function closeHelpDialog() {
  helpDialogRef.value?.close();
}

/**
 * Refreshes the activity feed after a manual event is created.
 *
 * @returns {Promise<void>}
 */
async function handleEventCreated() {
  await router.replace({ query: nextQuery({ page: 1 }) });
  await loadEvents();
}

/**
 * Maps sync status to a compact translated label.
 *
 * @param {CrmFeedEvent} event CRM event.
 * @returns {string}
 */
function syncStatusLabel(event) {
  const status = event.sync_status || 'received';
  return syncStatusLabels.value[status] || syncStatusLabels.value.received;
}

/**
 * Hides a broken product image while preserving the row.
 *
 * @param {CrmFeedEvent} event CRM event.
 * @returns {void}
 */
function markProductImageFailed(event) {
  failedProductImageEventIds.value = new Set([
    ...failedProductImageEventIds.value,
    imageFailureKey(event),
  ]);
}

/**
 * Returns contact secondary text.
 *
 * @param {CrmEventContact|null} contact Contact payload.
 * @returns {string}
 */
function contactSubtitle(contact) {
  return [contact?.email, contact?.phone_number].filter(Boolean).join(' / ');
}

onMounted(loadEvents);

watch(() => route.query, loadEvents);
</script>

<template>
  <main
    class="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col bg-n-background"
  >
    <header class="border-b border-n-weak px-6 py-4">
      <div class="flex items-start justify-between gap-4">
        <div class="flex flex-col gap-1">
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('CRM_EVENTS_FEED.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ t('CRM_EVENTS_FEED.SUBTITLE') }}
          </p>
        </div>
        <div class="flex shrink-0 flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-md border border-n-strong px-3 text-sm font-medium text-n-slate-12"
            @click="openHelpDialog"
          >
            <span class="i-lucide-info size-4" />
            {{ t('CRM_EVENTS_FEED.HELP.OPEN') }}
          </button>
          <button
            type="button"
            class="inline-flex h-9 items-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white"
            @click="openCreateDialog"
          >
            <span class="i-lucide-plus size-4" />
            {{ t('CRM_EVENT_CREATE.OPEN') }}
          </button>
        </div>
      </div>
    </header>

    <section class="min-h-0 flex-1 overflow-y-auto px-6 py-4">
      <div v-if="isLoading" class="flex justify-center py-12 text-n-slate-11">
        <Spinner />
      </div>
      <p v-else-if="errorMessage" class="text-sm text-n-ruby-11">
        {{ errorMessage }}
      </p>
      <p v-else-if="!events.length" class="text-sm text-n-slate-11">
        {{ t('CRM_EVENTS_FEED.NO_EVENTS') }}
      </p>
      <div v-else class="flex flex-col gap-5">
        <section
          v-for="group in groupedEvents"
          :key="group.key"
          class="flex flex-col gap-3"
        >
          <div class="flex items-center gap-3">
            <p
              data-testid="crm-events-group-label"
              class="shrink-0 text-xs font-semibold uppercase text-n-slate-10"
            >
              {{ group.label }}
            </p>
            <div class="h-px flex-1 bg-n-weak" />
          </div>
          <article
            v-for="event in group.events"
            :key="event.id"
            class="rounded-lg border border-n-weak bg-n-solid-1 p-4"
          >
            <div class="flex items-start gap-3">
              <a
                v-if="eventProductUrl(event) && !hasProductImageFailed(event)"
                :href="eventProductUrl(event)"
                target="_blank"
                rel="noopener noreferrer"
                class="mt-0.5 block size-12 shrink-0 overflow-hidden rounded-md border border-n-weak bg-n-alpha-2"
              >
                <img
                  v-if="eventProductImageUrl(event)"
                  :src="eventProductImageUrl(event)"
                  :alt="
                    productImageAltForEvent(event, t('CRM_EVENTS_FEED.EVENT'))
                  "
                  class="size-full object-cover"
                  @error="markProductImageFailed(event)"
                />
              </a>
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <h2 class="text-sm font-semibold text-n-slate-12">
                    {{ eventDisplayName(event) }}
                  </h2>
                  <span class="text-xs text-n-slate-10">
                    {{ formatOccurredAt(event.occurred_at) }}
                  </span>
                  <span
                    class="rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs font-medium text-n-slate-11"
                  >
                    {{ syncStatusLabel(event) }}
                  </span>
                </div>
                <p
                  v-if="eventMetadataSummary(event)"
                  class="mt-1 text-sm text-n-slate-11"
                >
                  {{ eventMetadataSummary(event) }}
                </p>
                <p
                  v-if="eventValueLabel(event)"
                  class="mt-1 text-sm text-n-slate-12"
                >
                  {{ eventValueLabel(event) }}
                </p>
                <div class="mt-3 flex items-center justify-between gap-3">
                  <div class="min-w-0">
                    <p class="text-xs font-medium uppercase text-n-slate-10">
                      {{ t('CRM_EVENTS_FEED.CONTACT') }}
                    </p>
                    <p class="truncate text-sm font-medium text-n-slate-12">
                      {{
                        event.contact?.name ||
                        t('CRM_EVENTS_FEED.UNKNOWN_CONTACT')
                      }}
                    </p>
                    <p
                      v-if="contactSubtitle(event.contact)"
                      class="truncate text-xs text-n-slate-11"
                    >
                      {{ contactSubtitle(event.contact) }}
                    </p>
                  </div>
                  <a
                    v-if="event.contact?.id"
                    data-testid="crm-event-contact-link"
                    :href="contactUrl(event)"
                    class="inline-flex h-8 shrink-0 items-center gap-2 rounded-md border border-n-strong px-2.5 text-xs font-medium text-n-slate-12"
                  >
                    <span class="i-lucide-user-round size-3.5" />
                    {{ t('CRM_EVENTS_FEED.VIEW_CONTACT') }}
                  </a>
                </div>
              </div>
            </div>
          </article>
        </section>
      </div>
    </section>

    <footer
      class="flex items-center justify-between border-t border-n-weak px-6 py-3"
    >
      <p class="text-xs text-n-slate-11">{{ paginationLabel }}</p>
      <div class="flex items-center gap-2">
        <button
          data-testid="crm-events-previous"
          type="button"
          class="inline-flex h-8 items-center gap-2 rounded-md border border-n-strong px-3 text-sm font-medium text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!hasPreviousPage || isLoading"
          @click="goToPage(currentPage - 1)"
        >
          <span class="i-lucide-chevron-left size-4" />
          {{ t('CRM_EVENTS_FEED.PREVIOUS') }}
        </button>
        <button
          data-testid="crm-events-next"
          type="button"
          class="inline-flex h-8 items-center gap-2 rounded-md border border-n-strong px-3 text-sm font-medium text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!hasNextPage || isLoading"
          @click="goToPage(currentPage + 1)"
        >
          {{ t('CRM_EVENTS_FEED.NEXT') }}
          <span class="i-lucide-chevron-right size-4" />
        </button>
      </div>
    </footer>
    <CrmEventCreateDialog
      ref="createDialogRef"
      require-contact-picker
      @created="handleEventCreated"
    />
    <Dialog
      ref="helpDialogRef"
      width="3xl"
      position="top"
      overflow-y-auto
      :title="t('CRM_EVENTS_FEED.HELP.TITLE')"
      :description="t('CRM_EVENTS_FEED.HELP.DESCRIPTION')"
      :show-cancel-button="false"
      :confirm-button-label="t('CRM_EVENTS_FEED.HELP.CLOSE')"
      @confirm="closeHelpDialog"
    >
      <div class="grid gap-3 md:grid-cols-2">
        <section
          v-for="section in helpSections"
          :key="section.key"
          class="flex flex-col gap-2 rounded-lg bg-n-alpha-2 p-3 outline outline-1 outline-n-container"
        >
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ section.title }}
          </h4>
          <ul class="flex flex-col gap-1 ps-4 text-sm text-n-slate-11">
            <li v-for="bullet in section.bullets" :key="bullet">
              {{ bullet }}
            </li>
          </ul>
        </section>
      </div>
    </Dialog>
  </main>
</template>
